import React, { useEffect } from 'react';
import { Sequence, AbsoluteFill, useCurrentFrame } from 'remotion';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';
import { calculateGridPosition } from '../Utils';
import { ImageMedia } from '../media/types';
import { PositionedItem, PositionedItemImpl } from './positioned';
import { AnimatedItem, AnimationConfig, SoundConfig } from './animated';
import { AnimationHelpers } from '../helpers';
import { SequenceTimingHelper } from '../helpers/SequenceTimingHelper';

export interface BrandingItem extends PositionedItem<ImageMedia>, AnimatedItem {}

export class BrandingItemImpl extends PositionedItemImpl<ImageMedia> implements BrandingItem {
    // AnimatedItem properties
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations: AnimationConfig[];
    customSoundEffects: SoundConfig[];

    constructor(branding: Omit<BrandingItem, 'render'>) {
        // Apply default gridArea only if no position is provided
        const brandingWithDefaults = {
            ...branding,
            gridArea: branding.position ? undefined : (branding.gridArea ?? { row: 4, col: 4, rows: 1, cols: 1, gridSize: 5 }),
            scale: Math.min(Math.max(branding.scale ?? 1, 0.5), 1)
        };
        super(brandingWithDefaults);
        // Initialize AnimatedItem properties
        this.entranceAnimation = branding.entranceAnimation;
        this.exitAnimation = branding.exitAnimation;
        this.entranceSoundEffect = branding.entranceSoundEffect;
        this.exitSoundEffect = branding.exitSoundEffect;
        this.customAnimations = branding.customAnimations ?? [];
        this.customSoundEffects = branding.customSoundEffects ?? [];
    }

    /**
     * Play sound effect (placeholder - integrate with audio system)
     */
    playSoundEffect(soundConfig: SoundConfig): void {
        console.log(`Playing sound effect: ${soundConfig.sound} at volume ${soundConfig.timing?.volume ?? 1}`);
    }

    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults): React.ReactNode {
        // Use centralized sequence timing calculation
        const { startFrame, durationFrames } = SequenceTimingHelper.calculateSequenceTiming(
            this.startAt,
            this.duration,
            fps,
            this.entranceAnimation,
            this.exitAnimation
        );

        // Create descriptive sequence name using centralized helper
        const sequenceName = SequenceTimingHelper.createSequenceName(
            'Branding',
            this.id,
            this.startAt,
            this.duration,
            this.entranceAnimation,
            this.exitAnimation
        );
        
        const sequenceProps = {
            key: this.id,
            name: sequenceName,
            from: startFrame,
            durationInFrames: durationFrames
        };

        const position = calculateGridPosition(
            this.gridArea,
            this.scale,
            this.layoutPadding ?? defaults?.layoutPadding ?? 0.05,
            this.paddingMode ?? 'container',
            this.position
        );

        return (
            <Sequence {...sequenceProps}>
                <AbsoluteFill>
                    <BrandingItemContent
                        branding={this}
                        position={position}
                        fps={fps}
                        theme={theme}
                        aspectRatio={aspectRatio}
                        defaults={defaults}
                    />
                </AbsoluteFill>
            </Sequence>
        );
    }
}

/**
 * BrandingItemContent component handles animation integration within Remotion context
 */
const BrandingItemContent: React.FC<{
    branding: BrandingItemImpl;
    position: React.CSSProperties;
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
}> = ({ branding, position, fps }) => {
    const currentFrame = useCurrentFrame();
    
    // Use centralized sequence animation styles helper
    // This handles both animated and non-animated branding consistently
    const { animationStyles, visibility } = SequenceTimingHelper.getSequenceAnimationStyles(
        currentFrame,
        fps,
        branding.duration,
        branding.entranceAnimation,
        branding.exitAnimation,
        undefined, // baseTransforms
        branding.opacity ?? 1 // default opacity for non-animated branding
    );
    
    // FIXED: Extract CSS animation classes from animation styles
    // The CSS animation system returns special properties that need to be applied as classes
    const cssAnimationClass = (animationStyles as any).animationClass;
    const cssAnimationPrefix = (animationStyles as any).animationPrefix || 'animate__';
    
    // Create clean CSS properties without the special class properties
    const cleanAnimationStyles = { ...animationStyles };
    delete (cleanAnimationStyles as any).animationClass;
    delete (cleanAnimationStyles as any).animationName;
    delete (cleanAnimationStyles as any).animationPrefix;
    
    // Build className for CSS animations
    let className = '';
    if (cssAnimationClass) {
        // Apply the base animate.css class and the specific animation class
        className = `${cssAnimationPrefix}animated ${cssAnimationClass}`;
        console.log(`[CSS_ANIMATION_FIX] Branding ${branding.id}: Applying CSS classes: ${className}`);
    }
    
    // Use effect to trigger entrance sound effects only when conditions change
    useEffect(() => {
        if (AnimationHelpers.shouldTriggerEntranceSound(
            currentFrame,
            fps,
            branding.startAt,
            branding.entranceAnimation,
            branding.entranceSoundEffect
        )) {
            branding.playSoundEffect(branding.entranceSoundEffect!);
        }
    }, [currentFrame, fps, branding.startAt, branding.entranceAnimation, branding.entranceSoundEffect]);
    
    // Use effect to trigger exit sound effects only when conditions change
    useEffect(() => {
        if (AnimationHelpers.shouldTriggerExitSound(
            currentFrame,
            fps,
            branding.startAt,
            branding.duration,
            branding.exitAnimation,
            branding.exitSoundEffect
        )) {
            branding.playSoundEffect(branding.exitSoundEffect!);
        }
    }, [currentFrame, fps, branding.startAt, branding.duration, branding.exitAnimation, branding.exitSoundEffect]);
    
    return (
        <div
            style={{
                position: 'absolute',
                ...position,
                opacity: branding.opacity,
                ...cleanAnimationStyles,
                ...visibility
            }}
            className={className}
        >
            {branding.mediaObject.render(fps, branding.id, branding.duration ?? 0, branding.objectFit)}
        </div>
    );
};